import { PaperOffset } from "paperjs-offset";
import {
  getAllPaths,
  getNearestDistancePingPong,
  getNearestNeighborPointSet,
  removeInnerPath,
} from "./path_fns.js";
import { each, go, map, some } from "fxjs/es";
import { consoleTime, getTime } from "./utils.js";

export const makePathsBridged = async (
  paper_path,
  bridge_width,
  is_view = null
) => {
  const paper_scope = paper_path._project._scope;
  let src_path = paper_path.clone();
  paper_path.visible = paper_path.selected = false;
  src_path.name = "bridged_item";
  if (paper_path instanceof paper_scope.CompoundPath) {
    const time = getTime();
    const control_size = Math.round(
      ((paper_scope.view.size.width + paper_scope.view.size.height) / 2) * 0.01
    );
    src_path = addBridgePathToOutline(
      src_path,
      control_size * 2,
      control_size * 2,
      bridge_width ? bridge_width : control_size * 1.5,
      control_size,
      true
    );
    // consoleTime(
    //   `Bridge Connection Total (control size: ${control_size})`,
    //   time,
    //   getTime()
    // );
  }
  paper_path.selected = false;
  paper_path.visible = false;
  if (!is_view) {
    src_path.selected = false;
    src_path.visible = false;
  }
  const bridged_item = {
    name: src_path.name,
    length: Math.round(src_path.length),
    area: Math.round(src_path.area),
    paper_obj: src_path,
    path_data: src_path.pathData,
  };
  // console.log("bridged item", bridged_item);
  return Promise.resolve(bridged_item);
};

const addBridgePathToOutline = (
  outline_paths,
  offset_range,
  offset_step,
  bridge_width,
  simp_factor,
  remove_colony
) => {
  const paper_scope = outline_paths._project._scope;
  let united_path;
  let path_arr = getAllPaths(outline_paths);
  if (path_arr.length > 1) {
    // console.log(
    //   "- Bridge Connection of",
    //   path_arr.length,
    //   "paths for",
    //   offset_range,
    //   "offset"
    // );
    if (path_arr.length <= 3) {
      //3개 이하면 clustering 발생이 불가능하다. 즉, 마지막 recursive step이 됨. 좀 더 offset을 과감하게 계산해서 한방에 주자.
      go(
        path_arr,
        map((path) => {
          const test = { test_path: path, compare_paths: [] };
          go(
            path_arr,
            each((compare_path) => {
              if (path !== compare_path) test.compare_paths.push(compare_path);
            })
          );
          return test;
        }),
        each((test) => {
          let nearest_distance = 0;
          go(
            test.compare_paths,
            each((compare_path) => {
              nearest_distance = getNearestDistancePingPong(
                compare_path,
                test.test_path
              );
            })
          );
          test.nearest_distance = test.test_path.data.nearest_distance = nearest_distance;
        })
      );
    }

    go(
      path_arr,
      each((test_path) => {
        //nn points를 찾기 위한 offsetting 알고리즘의 src 대상은 부정확해도 괜찮으므로 성능을 위해 simplification
        let simp_test_path = test_path.clone({ insert: false, deep: true });
        // 동적으로 path의 길이가 3개 이하면 좀 더 속도가 빠르지만 부정확한 flatten을 하고 좀 더 작은 단위로 세분화 되어 있을 때는
        // 정확도가 더 우선시 되므로 simplify로 속도보다 성능을 좀 더 가져간다.
        path_arr.length <= 4
          ? simp_test_path.flatten(simp_factor)
          : simp_test_path.simplify(simp_factor);
        if (simp_test_path.segments.length < 2) {
          //simplify 결과가 2 segment가 성립되지 못하면 offsetting이 불가능하므로 rollback
          simp_test_path = test_path.clone({ insert: false, deep: true });
        }
        if (test_path.data.nearest_distance) {
          offset_range = test_path.data.nearest_distance * 0.9;
          // offset_step = offset_range / 10;
          offset_step = 30;
        }
        getNearestBridgePath(
          path_arr,
          test_path,
          simp_test_path,
          offset_range,
          offset_step,
          bridge_width,
          "round"
        );
        if (test_path.data.bridge) {
          const bridge = test_path.data.bridge;
          united_path = !united_path ? test_path : united_path;
          united_path = united_path
            .unite(test_path, { insert: false })
            .unite(bridge, { insert: false });
          test_path.data = {};
        }
      })
    );
    removeInnerPath(united_path);
    united_path.copyAttributes(outline_paths);
    // outline_paths.replaceWith(united_path);
    outline_paths.remove();
    united_path.addTo(paper_scope.project.activeLayer);
    if (remove_colony && united_path instanceof paper_scope.CompoundPath) {
      united_path = addBridgePathToOutline(
        united_path,
        offset_range * 1.5,
        offset_step * 1.5,
        bridge_width,
        simp_factor,
        remove_colony
      );
    }
    return united_path;
  } else {
    return outline_paths;
  }
};

const inRange = (target_num, margin) => {
  return -margin <= target_num && target_num <= margin;
};

const getLocAvgOffset = (path, locations) => {
  return locations[0].point.getDistance(locations[locations.length - 1]);
};

const makePathStableAngle = (
  test_path,
  compare_path,
  bridge_path,
  two_points,
  angle_arr,
  angle_margin
) => {
  go(
    angle_arr,
    each((angle) => {
      if (inRange(angle, angle_margin)) {
        let a_crossing_locations = bridge_path.getCrossings(test_path);
        let b_crossing_locations = bridge_path.getCrossings(compare_path);
        if (a_crossing_locations.length && b_crossing_locations.length) {
          const a_dis = getLocAvgOffset(test_path, a_crossing_locations);
          const b_dis = getLocAvgOffset(compare_path, b_crossing_locations);
          const rotation_center = a_dis < b_dis ? two_points[0] : two_points[1];
          bridge_path.rotate(-angle, rotation_center);
        }
      }
    })
  );
};

const createBridgePath = (
  test_path,
  compare_path,
  two_points,
  bridge_width,
  join,
  angle_margin,
  is_insert = false
) => {
  const paper_scope = test_path._project._scope;
  const bridge_length = two_points[0].getDistance(two_points[1]);
  //brige 길이가 말도 안되게 작으면 생성 에러나므로 늘려줌
  if (bridge_length < 1) {
    const unit_tangent = two_points[1]
      .subtract(two_points[0])
      .divide(bridge_length);
    two_points[0] = two_points[0].add(unit_tangent.multiply(-1));
    two_points[1] = two_points[1].add(unit_tangent.multiply(1));
  }
  const connected_path = new paper_scope.Path(two_points, { insert: false });

  const angle =
    (Math.atan(
      (two_points[1].y - two_points[0].y) / (two_points[1].x - two_points[0].x)
    ) *
      180) /
    Math.PI;
  let bridge_path = PaperOffset.offsetStroke(
    PaperOffset.offsetStroke(connected_path, 0.1, {
      insert: false,
    }),
    bridge_width,
    {
      join: join,
      insert: is_insert,
    }
  );

  angle_margin &&
    makePathStableAngle(
      test_path,
      compare_path,
      bridge_path,
      two_points,
      [angle, angle + 90, angle - 90],
      angle_margin
    );
  connected_path.remove();
  if (bridge_path instanceof paper_scope.CompoundPath) {
    go(
      getAllPaths(bridge_path),
      each((path) => {
        if (!path.clockwise) {
          path.remove();
        } else {
          bridge_path = path;
        }
      })
    );
  }
  bridge_path.name = "bridge";
  bridge_path.selected = true;
  return bridge_path;
};

const getNearestBridgePath = (
  path_arr,
  test_path,
  simp_test_path,
  offset,
  offset_step,
  bridge_width,
  bridge_shape
) => {
  if (!test_path.data.bridge) {
    const offset_path = PaperOffset.offset(simp_test_path, offset, {
      insert: false,
    });
    go(
      path_arr,
      some((compare_path) => {
        if (test_path !== compare_path) {
          //crossing이 발생하면 순회 중단
          const crossing_loc_arr = offset_path.getCrossings(compare_path);
          // console.log("크로싱", crossing_loc_arr);
          if (
            crossing_loc_arr.length
            //getCrossings returns array of the locations of all crossings between the paths
          ) {
            const nn_points = getNearestNeighborPointSet(
              crossing_loc_arr,
              test_path,
              compare_path,
              false
            );

            const bridge_path = createBridgePath(
              test_path,
              compare_path,
              nn_points,
              bridge_width,
              bridge_shape,
              7,
              false
            );
            compare_path.data.bridge = test_path.data.bridge = bridge_path;
            return true;
          } else {
            return false;
          }
        }
      }),
      (intersect) => {
        if (!intersect) {
          getNearestBridgePath(
            path_arr,
            test_path,
            simp_test_path,
            offset + offset_step,
            offset_step,
            bridge_width,
            bridge_shape
          );
        }
      }
    );
  }
};
